import { Button, Menu, MenuHandler, MenuItem, MenuList, Typography } from '@material-tailwind/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AiFillPlusCircle, AiOutlineArrowRight } from 'react-icons/ai'
import { BiSolidDashboard } from 'react-icons/bi'
import { PiKeyLight, PiPathFill, PiUserCircleFill } from 'react-icons/pi'
import { Link, useLocation } from 'react-router-dom'
import Avatar from '../components/Avatar'
import RoleChip from '../components/RoleChip'
import { useDarkMode } from '../contexts/DarkModeContext'
import { useSessionContext } from '../contexts/SessionContext'
import { useFeatureAuth } from '../hooks/authorization'
import { FEATURES } from '../utils/constants/features.constants'
import { signOut } from '../utils/helpers/supabase.helpers'
import { ThemeSelector } from '../components/ThemeSelector'

// User type is defined inline since import is causing issues
interface User {
  id: string
  email: string
  name?: string
  surname?: string
  completeName?: string
  role: number | string
  finalized: boolean
  defaultExperienceId?: string
}

const Header: React.FC = () => {
  const { t } = useTranslation()
  const { user, refresh } = useSessionContext()
  const { pathname } = useLocation()

  const soundMapAuth = useFeatureAuth(FEATURES.SOUND_MAP)
  const uploadSpotsAuth = useFeatureAuth(FEATURES.UPLOAD_SPOTS_UI)
  const uploadExperienceAuth = useFeatureAuth(FEATURES.EXP_EDITOR_UI)
  const adminAuth = useFeatureAuth(FEATURES.ADMIN_PANEL)

  const { darkMode } = useDarkMode()

  const links = useMemo(() => {
    if (!user?.finalized) return []

    const userLinks = [
      {
        label: t('navigation.dashboard'),
        to: '/',
        icon: <BiSolidDashboard className="text-[18px]" />,
        testId: 'dashboard-nav-link',
        activeLinkCondition:
          pathname.includes('/experiences') ||
          pathname.includes('/patterns') ||
          pathname.includes('/access-codes'),
      },
    ]

    if (soundMapAuth) {
      userLinks.push({
        label: t('navigation.explore'),
        to: '/experience',
        icon: <PiPathFill className="text-[18px]" />,
        testId: 'explore-nav-link',
        activeLinkCondition: false,
      })
    }

    if (uploadSpotsAuth || uploadExperienceAuth) {
      userLinks.push({
        label: t('navigation.upload'),
        to: '/upload',
        icon: <AiFillPlusCircle className="text-[18px]" />,
        testId: 'upload-nav-link',
        activeLinkCondition: false,
      })
    }

    return userLinks
  }, [user, soundMapAuth, uploadSpotsAuth, uploadExperienceAuth, pathname, t])

  const actions = useMemo(() => {
    if (user) return []

    if (process.env.REACT_APP_COMING_SOON_PREVIEW) {
      return [
        {
          label: 'Available now for beta testing!',
          to: 'mailto:agami@mezzoforte.design',
          testId: 'signin-btn',
        },
        {
          label: 'CONTACT US',
          to: 'mailto:agami@mezzoforte.design',
          variant: 'text',
          testId: 'create-account-btn',
        },
      ]
    }

    return [
      {
        label: t('auth.signIn'),
        to: '/signin',
        variant: 'outlined',
        testId: 'signin-btn',
      },
      {
        label: t('auth.createAccount'),
        to: '/register',
        testId: 'create-account-btn',
      },
    ]
  }, [user, t])

  const logout = async () => {
    const { error } = await signOut()
    if (error) {
      return
    }
    refresh()
  }

  // Détermine la classe de fond en fonction du mode et de l'état de l'utilisateur
  const getNavBackgroundClass = () => {
    if (!user && pathname === '/') {
      return darkMode ? 'bg-gray-900/80' : 'bg-transparent'
    }
    return darkMode ? 'bg-primary' : 'bg-primary'
  }

  // Couleur du texte en fonction du mode
  const textColorClass = darkMode ? 'text-white' : 'text-white-yellow'

  return (
    <nav
      className={`
        w-full
        flex justify-between items-center
        px-4 md:px-16 py-4 z-50
        top-0 fixed
        ${getNavBackgroundClass()} ${textColorClass}`}
      style={!user && pathname === '/' ? { position: 'absolute' } : {}}
    >
      <div className="w-full md:w-1/2">
        <span className="inline-block">
          <Link to="/">
            <img
              className="h-10 opacity-90 hover:opacity-100"
              alt="agami logo"
              src="https://mezzoforte.design/img/agami/agami_logo_horiz_accent.png"
            />
          </Link>
        </span>
      </div>

      <div className="space-x-12 items-center flex">
        {links && links.length > 0 && (
          <div className="space-x-12 hidden md:flex">
            {links.map((link) => {
              const linkIsActive = link.activeLinkCondition || `/${pathname.split('/')[1]}` === link.to
              let linkTextColorClass = ''
              if (linkIsActive) {
                linkTextColorClass = darkMode ? 'text-accent-300' : 'text-accent'
              }
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`opacity-90 hover:opacity-100 ${linkTextColorClass}`}
                  data-testid={link.testId}
                >
                  {link.label}
                </Link>
              )
            })}
          </div>
        )}

        {actions && actions.length > 0 ? (
          <div className="flex space-x-2 w-full">
            {actions.map((action) => (
              <Link key={action.to} to={action.to}>
                <Button
                  variant={action.variant as 'outlined' | 'text' | undefined}
                  size="sm"
                  data-testid={action.testId}
                  className={darkMode ? 'dark-mode-btn' : ''}
                  style={{
                    textWrap: 'nowrap',
                  }}
                >
                  {action.label}
                </Button>
              </Link>
            ))}
          </div>
        ) : (
          <ThemeSelector />
        )}

        {!!user && user.finalized && (
          <Menu>
            <MenuHandler>
              <Button variant="text" className="p-0" data-testid="user-menu-dropdown-btn">
                <Avatar
                  name={(user as User).name || ''}
                  surname={(user as User).surname || ''}
                  size="sm"
                  interactive
                />
              </Button>
            </MenuHandler>
            <MenuList
              className={`${darkMode ? 'bg-primary text-white' : 'bg-white-yellow text-primary'}`}
              data-testid="user-menu-dropdown"
            >
              <div
                className={`w-full pt-[9px] pb-3 px-3 border-b ${
                  darkMode ? 'border-gray-700' : 'border-gray-200'
                }`}
              >
                <Typography className="font-bold">{user?.completeName}</Typography>
                <span className="inline-block">
                  <RoleChip role={user.role} />
                </span>
              </div>
              {links.map((link) => (
                <Link key={link.to} to={link.to}>
                  <MenuItem className="flex md:hidden space-x-2 items-center hover:bg-white-yellow-700 dark:hover:bg-primary-300">
                    {link.icon}
                    <Typography variant="paragraph">{link.label}</Typography>
                  </MenuItem>
                </Link>
              ))}

              <Link to="/profile">
                <MenuItem className="flex space-x-2 items-center hover:bg-white-yellow-700 dark:hover:bg-primary-300">
                  <PiUserCircleFill className="text-[18px] dark:text-white" />
                  <Typography variant="paragraph">{t('navigation.profile')}</Typography>
                </MenuItem>
              </Link>
              {adminAuth && (
                <Link to="/admin">
                  <MenuItem className="flex space-x-2 items-center hover:bg-white-yellow-700 dark:hover:bg-primary-300">
                    <PiKeyLight className="text-[18px] dark:text-white" />
                    <Typography variant="paragraph">{t('navigation.admin')}</Typography>
                  </MenuItem>
                </Link>
              )}
              <MenuItem
                className="flex space-x-2 items-center hover:bg-white-yellow-700 dark:hover:bg-primary-300"
                onClick={logout}
              >
                <AiOutlineArrowRight className="text-[18px] dark:text-white" />
                <Typography variant="paragraph">{t('auth.signOut')}</Typography>
              </MenuItem>
            </MenuList>
          </Menu>
        )}
      </div>
    </nav>
  )
}

export default Header
